// frontend/src/components/common/DateCellEditor.jsx
//
// The AG Grid cell editor of every `dateColumn` (hooks/useColumnTypes.js): the
// app's AppDatePicker, filling the cell. Opened by a double click, Enter or
// F2, it shows its calendar; opened by typing, the typed character is the
// field's first keystroke and the calendar stays shut. Picking a day in the
// calendar commits the edit; a typed day commits on Enter, or on leaving the
// cell in a grid with `editableGridProps`. A typed day the field refuses (red:
// unfinished, or outside the column's `cellEditorParams` minDate / maxDate)
// never reaches the grid, so ending the edit then keeps the old day, as Escape
// does.
//
// The calendar is portalled onto <body>, outside the cell, so under
// `stopEditingWhenCellsLoseFocus` AG Grid would read focus moving into it as
// leaving the cell and end the edit; the `ag-custom-component-popup` class
// marks it as the editor's own (AG Grid "Popup Editors"). `dialog` is the
// same calendar on a touch screen.

import { useEffect, useRef, useState } from 'react';
import AppDatePicker from './AppDatePicker';

const EDITOR_POPUP = { className: 'ag-custom-component-popup' };

// MUI's field takes a keystroke as an `input` event on the section it lands in
// (@mui/x-date-pickers useFieldSectionContentProps `handleInput`), and only
// once it has selected that section: `autoFocus` selects the first one, the
// first part of the user's date format.
function typeIntoFirstSection(field, character) {
  const section = field.querySelector('.MuiPickersSectionList-sectionContent');
  section.textContent = character;
  section.dispatchEvent(new Event('input', { bubbles: true }));
}

function DateCellEditor({ value, onValueChange, stopEditing, colDef, eventKey, minDate, maxDate }) {
  const typedFirst = eventKey?.length === 1 ? eventKey : null;
  const [open, setOpen] = useState(typedFirst == null);
  const [sectionSelected, setSectionSelected] = useState(false);
  const fieldRef = useRef(null);
  const untyped = useRef(typedFirst);

  useEffect(() => {
    if (!sectionSelected || untyped.current == null) return;
    typeIntoFirstSection(fieldRef.current, untyped.current);
    untyped.current = null;
  }, [sectionSelected]);

  return (
    <AppDatePicker
      ariaLabel={colDef.headerName}
      value={value}
      onChange={onValueChange}
      onAccept={() => stopEditing()}
      minDate={minDate}
      maxDate={maxDate}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      autoFocus
      onSelectedSectionsChange={(sections) => setSectionSelected(sections != null)}
      slotProps={{
        popper: EDITOR_POPUP,
        dialog: EDITOR_POPUP,
        textField: { ref: fieldRef, className: 'grid-date-editor', sx: { width: '100%', height: '100%' } },
      }}
    />
  );
}

export default DateCellEditor;
